import { gamePresentationOf } from "@softmax/web-features/coworlds";

/** Creator artwork and copy, keyed by corpus root. Other games use their published store page. */
type OnePagerLink = { readonly label: string } & (
  | { readonly kind: "external"; readonly href: string }
  | {
      readonly kind: "page";
      readonly destination: { readonly name: "game.playMarkdown" };
    }
  | {
      readonly kind: "page";
      readonly destination: {
        readonly name: "docs";
        readonly slug: readonly string[];
      };
    }
);

export type OnePagerCoworld = {
  readonly name: string;
  /** One or two lines; what the game is, in the creator's terms. Absent reads the store page's blurb. */
  readonly pitch?: string;
  /** The creator's real home: the big "Visit" link. */
  readonly site?: { readonly label: string; readonly href: string };
  readonly links: readonly OnePagerLink[];
  readonly art: {
    /** A square-ish mark; absent draws the monogram. */
    readonly logo?: string | undefined;
    /**
     * The picture behind the header: a real replay moment captured by
     * `scripts/capture-headers.mjs` into `public/home/headers/<file>-*`, a
     * 4:1 frame for wide screens and a 16:9 one for phones. `focal` is the
     * CSS object-position that keeps the action in frame when the band
     * crops it. Absent draws `still`, else the accent gradient.
     */
    readonly cover?: {
      readonly file: string;
      readonly focal: string;
      readonly focalPhone: string;
    };
    /** One of the coworld's real-game stills (`../lib/cover-art.ts`), for a coworld with no header capture. */
    readonly still?: string | undefined;
    /** The coworld's own colour, used for the one accent the template allows. */
    readonly accent: string;
    readonly monogram?: string;
  };
  /**
   * The league's starter programs (seed/filler) whose public source is the
   * coworld manifest's own player: starter policy name -> manifest player id.
   * The API does not link a starter version to its source (its artifact hash
   * is team-only), so each entry is checked by hand against the published
   * manifest and the public repo. A starter not listed here gets no "Start
   * from this".
   */
  readonly starters?: Record<string, string>;
};

const onePagerCoworlds: Record<string, OnePagerCoworld> = {
  "gods-of-the-arena": {
    name: "Gods of the Arena",
    pitch:
      "A five-a-side hero arena: draft gods, buy items, push lanes. Your cogent's program plays every seat it is given.",
    site: {
      label: "softmax-universe.github.io/GOTA",
      href: "https://softmax-universe.github.io/polyworld-buff/GOTA/",
    },
    links: [],
    art: {
      logo: "/coworlds/gods-of-the-arena/logo.webp",
      cover: {
        file: "gods-of-the-arena",
        focal: "50% 40%",
        focalPhone: "62% 50%",
      },
      accent: "#b4441f",
    },
    // Checked 2026-10-16: the manifest's `baseline` file (sha256 6b226d1d…)
    // is byte-identical to Metta-AI/polyworld (public)
    // examples/gods_of_the_arena/players/base.bas, and both this starter and
    // the coworld are Andre von Houck's (treeform).
    starters: { "Polyworld GOTA Baseline Filler": "baseline" },
  },
  "generals-competition": {
    name: "Generals",
    pitch:
      "Three to eight generals compete under fog of war. Grow armies, capture rival generals, and climb the FFA Survival ladder. Classic 1v1 and Build 1v1 are also available.",
    site: { label: "generals.bot", href: "https://generals.bot" },
    links: [
      {
        label: "Docs",
        kind: "page",
        destination: { name: "docs", slug: ["guides", "generals-game"] },
      },
    ],
    art: {
      logo: "/home/icons/generals.svg",
      cover: { file: "generals", focal: "50% 50%", focalPhone: "50% 50%" },
      accent: "#2f5fb3",
    },
  },
  webdiplomacy: {
    name: "webDiplomacy",
    pitch:
      "Classic Diplomacy on the unmodified webDiplomacy server: seven powers, simultaneous orders, alliances that have to be negotiated.",
    site: { label: "webdiplomacy.net", href: "https://webdiplomacy.net" },
    links: [
      {
        label: "Rules",
        href: "https://webdiplomacy.net/rules.php",
        kind: "external",
      },
    ],
    art: {
      accent: "#7a5c2e",
      logo: "/home/icons/webdiplomacy.png",
      monogram: "wD",
      cover: { file: "webdiplomacy", focal: "50% 50%", focalPhone: "50% 50%" },
    },
  },
};

/** The one-pager's entry for a corpus address (`generals-competition/ffa` reads `generals-competition`). */
export function onePagerCoworldOf(corpus: string): OnePagerCoworld | null {
  return onePagerCoworlds[gamePresentationOf(corpus).key] ?? null;
}
